<template>
  <v-content>
    <v-container v-if="loadingInitialData">
      <v-progress-linear indeterminate></v-progress-linear>
    </v-container>
    <v-card v-else elevation="0" :min-height="cardHeight">
      <v-list>
        <v-list-tile class="tileTitle" avatar>
          <v-list-tile-avatar
            size=32
            tile>
            <img src="https://bunny.playposit.com/images/add_to_googleclassroom.png" alt="Google Classroom Logo">
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              <span class="text-white text-truncate-popover">{{cardTitle}}</span>
            </v-list-tile-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
      <v-alert
        id="classPickerAlert"
        v-model="showCourseAlert"
        type="warning"
        dismissible>
        The course you are trying to assign to is not associated with your current Google account or has been archived!
      </v-alert>
      <v-container
        v-if="!assignmentCreated && !loadingInitialData && googleClassroomClasses.courses.length && !alreadyAssigned">
        <v-select
        v-model="selectedClass"
        label="Select Class"
        :items="googleClassroomClasses.courses"
        item-text="name"
        item-value="id"
        :loading="loadingData"
        color="#2196F3"
        ></v-select>
        <div v-if="selectedClass">
          <v-text-field
          v-model="assignmentTitle"
          label="Title"
          @input="$v.assignmentTitle.$touch()"
          :error-messages="assignmentTitleErrors">
          </v-text-field>
          <v-textarea
          v-model="assignmentDescription"
          label="Description"
          placeholder="Complete this content and complete any interactions in it."
          rows=3>
          </v-textarea>
          <v-layout row justify-start>
            <v-flex>
              <h3>Due Date</h3>
              <v-date-picker
                v-model="dueDate"
                color="#2196F3"
                no-title
                @input="$v.dueTime.$touch()"></v-date-picker>
            </v-flex>
            <v-flex justify-start>
              <v-text-field
                :error-messages="maxPointErrors"
                label="Max Points"
                v-model="maxPoints"
                type="number"
                @input="$v.maxPoints.$touch()"></v-text-field>
                <v-text-field
                  v-show="dueDate"
                  :label="dueTimeLabel"
                  type="time"
                  v-model="dueTime"></v-text-field>
            </v-flex>
          </v-layout>
          <v-text-field
          v-model="assignmentLink"
          readonly
          label="Assignment Link"
          class="mt-2">
          </v-text-field>
          <v-layout row justify-end align-baseline>
            <v-btn
            dark
            color="#2196F3"
            @click="handleCreateAssignment"
            :loading="loadingData"
            :disabled="$v.$anyError">Create Assignment</v-btn>
          </v-layout>
        </div>
      </v-container>
      <v-container v-else-if="assignmentCreated">
        <h3>You have successfully created a Google Classroom assignment.  You can close this window or visit your Google Classroom using the link below.</h3>
        <v-layout
          row
          justify-center
          align-baseline
          class="mt-3">
          <v-btn
            dark
            color="#2196F3"
            :href="googleClassroomURL">Launch Google Classroom</v-btn>
        </v-layout>
      </v-container>
      <v-container v-else-if="alreadyAssigned">
        <h3>This content has already been assigned to a Google Classroom class.  You can access the assignment using the button below.  If you need to make changes or delete the assignment, do so in Google Classroom first.  Then try to create another assignment here.</h3>
        <v-layout
          row
          justify-center
          align-baseline
          class="mt-3">
          <v-btn
            dark
            link
            color="#2196F3"
            :href="alreadyAssigned">Launch Google Classroom</v-btn>
        </v-layout>
      </v-container>
      <v-container
        v-else>
        <h3>There aren't any active Google Classroom courses associated with this account.  Try creating a course in Google Classroom or switching to a different account.</h3>
      </v-container>
      <v-card-actions>
        <p>Currently logged in as {{googleClassroomClasses.user.emailAddress}}. <a @click="loadClasses(true)">Change account?</a></p>
      </v-card-actions>
    </v-card>
  </v-content>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import storejs from 'store';
import _ from 'lodash';
import { validationMixin } from 'vuelidate';
import { required, numeric, integer, between } from 'vuelidate/lib/validators';

export default {
  name: 'GoogleClassroomAssignmentPicker',
  mixins: [validationMixin],
  validations: {
    assignmentTitle: {
      required,
    },
    maxPoints:{
      required,
      numeric,
      integer,
      between: between(0, 100),
    }
  },
  data() {
    return {
      assignmentCreated: false,
      assignmentDescription: '',
      assignmentLink: '',
      assignmentTitle: '',
      createdAssignment: null,
      dueDate: null,
      dueTime: '17:00',
      googleClassroomClasses: [],
      loadingInitialData: true,
      loadingData: true,
      localTimeZone: null,
      maxPoints: 100,
      maxPointRules: [
    ],
      queryParams: null,
      selectedClass: null,
      showCourseAlert: false,
    };
  },
  computed: {
    alreadyAssigned() {
      return _.get(this.googleClassroomClasses, 'alternateLink', false);
    },
    assignmentTitleErrors() {
      const errors = [];
      if (!this.$v.assignmentTitle.$dirty) return errors;
      if (!this.$v.assignmentTitle.required) {
        errors.push('Title is required.');
      }
      return errors;
    },
    contentURL() {
      if (this.queryParams.bulbId) return `${window.location.origin}/go/Z29vZ2xl/${this.queryParams.bulbId}/0/${this.queryParams.sectionId}/0`;
      return `${window.location.origin}/go/Z29vZ2xl/${this.queryParams.playlistId}/0/${this.queryParams.sectionId}/0/${this.queryParams.playlistName}`
    },
    cardHeight() {
      return this.assignmentCreated ? 250 : 500;
    },
    cardTitle() {
      if (this.assignmentCreated) return 'Assignment Created!';
      if (this.alreadyAssigned) return 'Assignment Already Created';
      return 'Create Assignment';
    },
    dueTimeLabel() {
      if (this.localTimeZone) return `Due Time - ${this.localTimeZone}`;
      return 'Due Time';
    },
    formattedDueDate() {
      if (!this.dueDate) return {};
      const splitDate = this.dueDate.split('-');
      return {
        year: splitDate[0],
        month: splitDate[1],
        day: splitDate[2],
      };
    },
    formattedDueTime() {
      if (!this.dueDate) return {};
      const splitTime = this.dueTime.split(':');
      const timezoneOffset = new Date().getTimezoneOffset() / 60;
      return {
        hours: parseInt(splitTime[0], 10) + parseInt(timezoneOffset, 10),
        minutes: splitTime[1],
        seconds: 0,
        nanos: 0,
      };
    },
    googleClassroomURL() {
      return _.get(this, 'createdAssignment.data.alternateLink', '');
    },
    maxPointErrors() {
      const errors = [];
      if (!this.$v.maxPoints.$dirty) return errors;
      if (!this.$v.maxPoints.required) {
        errors.push('Max Points is required.');
      }
      if (!this.$v.maxPoints.numeric) {
        errors.push('Max Points must be a whole number');
      }
      if (!this.$v.maxPoints.integer) {
        errors.push('Must be a whole number.');
      }
      if (!this.$v.maxPoints.between) {
        errors.push('Must be between 0 and 100.');
      }
      return errors;
    },
  },
  methods: {
    ...mapMutations([
      'triggerSnackbar',
    ]),
    ...mapActions('instructors', [
      'createGoogleClassroomAssignment',
      'getGoogleClassroomData',
    ]),
    formatAssignedDate() {
      if (!this.queryParams.assignedDate) return null;
      const date = new Date(this.queryParams.assignedDate);
      this.dueDate = date.toISOString().split('T')[0];
      const formattedTime = date.toLocaleTimeString('en-US', {hour: '2-digit', minute:'2-digit', hour12: false}).split(' ')[0];
      this.dueTime = formattedTime === '24:00' ? '00:00' : formattedTime; // Vuetify's old datePicker can't handle 24hr time well
    },
    async handleCreateAssignment() {
      try {
        this.loadingData = true;
        const courseWork = {
          courseId: this.selectedClass,
          title: this.assignmentTitle,
          description: this.assignmentDescription,
          maxPoints: this.maxPoints,
          materials: [
            {
              link: {
                url: this.contentURL,
              },
            },
          ],
          workType: 'ASSIGNMENT',
          state: 'PUBLISHED',
        };
        if (this.dueDate) courseWork.dueDate = this.formattedDueDate;
        if (this.dueDate) courseWork.dueTime = this.formattedDueTime;
        this.createdAssignment = await this.createGoogleClassroomAssignment({
          courseWork: courseWork,
          sectionId: this.queryParams.sectionId,
          token: storejs.get('pp_instructor_token'),
          contentId: this.queryParams.bulbId || this.queryParams.playlistId,
          contentType: this.queryParams.bulbId ? 'bulb' : 'playlist',
        });
        dataLayer.manualLog(`googleClassroom: assignment created`, 'googleClassroom');
        this.assignmentCreated = true;
      } catch(e) {
        this.triggerSnackbar({
          text: 'Error creating assignment.',
          color: 'error',
          timeout: 5000,
        });
      } finally {
        this.loadingData = false;
      }
    },
    async loadClasses(switchAccount = '') {
      try {
        this.showCourseAlert = false;
        const classroomData = await this.getGoogleClassroomData(
        {
          token: storejs.get('pp_instructor_token'),
          state: encodeURIComponent(_.get(this.$route, 'query.state')),
          code: _.get(this.$route, 'query.code', ''),
          redirectURL: window.location.href.split('?')[0],
          switchAccount,
        });
        if (typeof classroomData.data === 'string' && classroomData.data.includes('accounts.google.com/o/oauth2')) {
          window.location.replace(classroomData.data);
        } else {
          this.googleClassroomClasses = classroomData.data;
          const googleCourseId = _.get(this.queryParams, 'googleCourseId', null);
          if (_.find(this.googleClassroomClasses.courses, { id: googleCourseId })) {
            this.selectedClass = googleCourseId;
          } else {
            this.showCourseAlert = true;
          }
          this.loadingInitialData = false;
          this.loadingData = false;
        }
      } catch(e) {
        this.triggerSnackbar({
          text: 'Error getting class data.',
          color: 'error',
          timeout: 5000,
        });
      }
    }
  },
  beforeMount() {
    this.loadClasses();
  },
  mounted() {
    let state = decodeURIComponent(this.$route.query.state);
    this.queryParams = JSON.parse(state);
    this.assignmentTitle = this.queryParams.bulbName || this.queryParams.playlistName;
    this.assignmentLink = this.contentURL;
    this.formatAssignedDate();
    this.localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // In certain edge cases the modal was cut off and unscrollable.
    document.body.className += 'assignCardContent';
  },
};
</script>

<style>
  .tileTitle {
    background-color: #2196F3;
  }
  
  #classPickerAlert .v-icon--right,
  #classPickerAlert div {
    color: white;
  }

  .assignCardContent {
    height: 100%;
    overflow: auto;
  }
</style>
